import React, { useMemo, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
	commitRutField,
	INVALID_RUT_MESSAGE,
	isLoginRutNumericMode,
	RUT_DIGITS_HINT,
	sanitizeRutDigitsOnly,
} from "../utils/rutUtils";

const ROLE_BY_PATH = {
	"/login-est": { label: "Estudiante", act_as: "Student", after: "/portal" },
	"/login-doc": { label: "Docente", act_as: "Teacher", after: "/portal-doc" },
	"/login-emp": {
		label: "Representante de la Organización",
		act_as: "CompanyAdmin",
		after: "/portal-emp",
	},
	"/login": { label: "Administrador", act_as: "Admin", after: "/backoffice" },
};

function Login() {
	const location = useLocation();
	const navigate = useNavigate();
	const { login } = useAuth();

	const roleInfo = useMemo(
		() => ROLE_BY_PATH[location.pathname] || ROLE_BY_PATH["/login"],
		[location.pathname],
	);

	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [keepSession, setKeepSession] = useState(() => {
		try {
			return localStorage.getItem("login_keep_session") === "true";
		} catch {
			return false;
		}
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [usernameRutError, setUsernameRutError] = useState(null);
	const [fieldTouched, setFieldTouched] = useState({ username: false, password: false });
	const [submitAttempted, setSubmitAttempted] = useState(false);

	const usernameRequiredError = "Debes ingresar usuario o RUT.";
	const passwordRequiredError = "Debes ingresar la contraseña.";

	const handleLogin = async (e) => {
		e.preventDefault();
		setSubmitAttempted(true);
		setError(null);
		setUsernameRutError(null);
		const usernameTrimmed = username.trim();
		if (!usernameTrimmed || !password) {
			return;
		}

		let identifier = usernameTrimmed;
		if (identifier && isLoginRutNumericMode(username)) {
			const r = commitRutField(username);
			if (!r.ok) {
				setUsernameRutError(r.message || INVALID_RUT_MESSAGE);
				return;
			}
			if (r.formatted) {
				identifier = r.formatted;
				setUsername(r.formatted);
			}
		}

		setLoading(true);

		try {
			try {
				localStorage.setItem(
					"login_keep_session",
					keepSession ? "true" : "false",
				);
			} catch { }
			// 1) JWT login para todos los roles (act_as según ruta); keep_session define duración de la cookie
			await login(identifier, password, roleInfo.act_as, keepSession);

			// 3) Otros roles: navegar según after
			if (roleInfo.after) {
				navigate(roleInfo.after, { replace: true });
				return;
			}

			// fallback razonable
			navigate("/", { replace: true });
		} catch (err) {
			//limpiar inputs
			setUsername("");
			setPassword("");
			setUsernameRutError(null);
			setFieldTouched({ username: false, password: false });
			setSubmitAttempted(false);

			let msg =
				err?.response?.data?.act_as ||
				err?.response?.data?.detail ||
				err?.response?.data?.error ||
				"Credenciales inválidas o rol no permitido.";

			if (msg === "No active account found with the given credentials") {
				msg = "Usuario o contraseña incorrectos.";
			}

			setError(typeof msg === "string" ? msg : JSON.stringify(msg));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="login-page font-n">
			<Link to="/" className="login-back-home">
				<i
					className="ri-arrow-go-back-line d-inline d-md-none"
					aria-hidden
				/>
				<span className="d-none d-md-inline">Volver</span>
			</Link>
			<div className="login-image-section">
				<img src="/img/carousel/mujer.webp" alt="Edificio Somnus" />
			</div>

			<div className="login-form-section">
				<div className="login-header">
					<Link to="/" className="logo-link-container">
						<img
							src="/img/somnus.webp"
							alt="Instituto de Capacitación"
							className="login-logo"
						/>
						<div className="login-title">
							<span className="logo-somnus-login">Somnus</span>
							<p className="sub-txt">
								Institución de Capacitación
							</p>
						</div>
					</Link>
					<div className="sesion-login">
						<p>{roleInfo.label}</p>
					</div>
				</div>

				<form className="form-container" onSubmit={handleLogin}>
					<p className="form-label">Usuario o RUT:</p>
					{(submitAttempted || fieldTouched.username) && !username.trim() && (
						<div className="label-error">{usernameRequiredError}</div>
					)}

					<input
						type="text"
						placeholder="Usuario o RUT (ej. 123456789)"
						title={RUT_DIGITS_HINT}
						className={`login-input ${(usernameRutError || ((submitAttempted || fieldTouched.username) && !username.trim())) ? "input-error" : ""}`}
						value={username}
						onChange={(e) => {
							const v = e.target.value;
							if (isLoginRutNumericMode(v)) {
								setUsername(sanitizeRutDigitsOnly(v));
							} else {
								setUsername(v);
							}
							if (error) setError(null);
							if (usernameRutError) setUsernameRutError(null);
						}}
						onBlur={(e) => {
							setFieldTouched((prev) => ({ ...prev, username: true }));
							const prev = e.target.value;
							if (!prev.trim()) {
								setUsernameRutError(null);
								return;
							}
							if (!isLoginRutNumericMode(prev)) {
								setUsernameRutError(null);
								return;
							}
							const r = commitRutField(prev);
							if (!r.ok) {
								setUsernameRutError(
									r.message || INVALID_RUT_MESSAGE,
								);
								return;
							}
							setUsernameRutError(null);
							if (r.formatted) setUsername(r.formatted);
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.currentTarget.blur();
							}
						}}
						autoComplete="username"
					/>
					<p className="fs-6 text-muted">
						Si ingresas RUT, solo números (y K si aplica); al salir
						o Enter se valida el dígito verificador y se formatea.
					</p>
					<input
						type="password"
						placeholder="••••••••"
						className={`login-input ${((submitAttempted || fieldTouched.password) && !password) ? "input-error" : ""}`}
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							if (error) setError(null);
						}}
						onBlur={() => setFieldTouched((prev) => ({ ...prev, password: true }))}
						autoComplete="current-password"
					/>
					{(submitAttempted || fieldTouched.password) && !password && (
						<div className="label-error">{passwordRequiredError}</div>
					)}
					{(usernameRutError || error) && (
						<div className="label-error">
							{usernameRutError || error}
						</div>
					)}
					<div className="remember-me">
						<input
							type="checkbox"
							id="keep-session"
							checked={keepSession}
							onChange={(e) => setKeepSession(e.target.checked)}
							aria-describedby="keep-session-desc"
						/>
						<label htmlFor="keep-session" id="keep-session-desc">
							Mantener la sesión iniciada
						</label>
					</div>

					<button
						type="submit"
						className="login-btn"
						disabled={loading}
					>
						{loading ? "Ingresando..." : "Iniciar sesión"}
					</button>

					<Link to="/recuperar-clave" className="forgot-password">
						Recuperar clave. Haz clic <span>aquí.</span>
					</Link>
				</form>
				<div className="login-footer">
					<Link to="/privacidad">Reglamento y políticas</Link>
				</div>
			</div>
		</div>
	);
}

export default Login;
